import { useState, useEffect } from 'react';
import { UserProfilePopover } from '@/components/chat/UserProfilePopover';
import { useAuthStore } from '@/stores/authStore';

// Simple cache for user data to avoid repeated API calls
const userCache = new Map<string, { name: string; username: string }>();

/**
 * Parse mentions in message content
 * Converts <@userId> to clickable mention tags with popover
 *
 * Example:
 * Input: "Oi <@uuid-123> tudo bem?"
 * Output: ["Oi ", <MentionTag userId="uuid-123" />, " tudo bem?"]
 */
export function parseMentions(content: string) {
  // Support both underscore IDs (user_xxx) and UUID format
  const mentionRegex = /<@([a-zA-Z0-9_-]+)>/g;
  const parts: (string | React.ReactNode)[] = [];
  let lastIndex = 0;
  let match;

  while ((match = mentionRegex.exec(content)) !== null) {
    const [fullMatch, userId] = match;
    const matchIndex = match.index;

    // Add text before mention
    if (matchIndex > lastIndex) {
      parts.push(content.substring(lastIndex, matchIndex));
    }

    // Add mention component
    parts.push(
      <MentionTag key={`mention-${userId}-${matchIndex}`} userId={userId} />
    );

    lastIndex = matchIndex + fullMatch.length;
  }

  // Add remaining text
  if (lastIndex < content.length) {
    parts.push(content.substring(lastIndex));
  }

  return parts.length > 0 ? parts : [content];
}

/**
 * Mention Tag Component
 * Renders a clickable @username with popover
 * Fetches user data on mount to display real name
 */
interface MentionTagProps {
  userId: string;
}

function MentionTag({ userId }: MentionTagProps) {
  const [userData, setUserData] = useState<{ name: string; username: string } | null>(
    userCache.get(userId) || null
  );
  const [loading, setLoading] = useState(!userCache.has(userId));
  const token = useAuthStore.getState().token;

  useEffect(() => {
    // Skip if already cached
    if (userCache.has(userId)) {
      setUserData(userCache.get(userId)!);
      setLoading(false);
      return;
    }

    // Fetch user data
    const fetchUser = async () => {
      try {
        const mkeyUrl = import.meta.env.VITE_MKEY_API_URL || 'https://mkey-api-production.fidelismadboat.workers.dev';
        const response = await fetch(`${mkeyUrl}/api/users/${userId}/profile`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.user) {
            // Build display name from first_name + last_name or fallback
            let displayName = 'Tripulante';
            if (data.user.first_name && data.user.last_name) {
              displayName = `${data.user.first_name} ${data.user.last_name}`;
            } else if (data.user.full_name) {
              displayName = data.user.full_name;
            } else if (data.user.name) {
              displayName = data.user.name;
            } else if (data.user.username) {
              displayName = data.user.username;
            }

            const userData = {
              name: displayName,
              username: data.user.username || userId.slice(0, 8),
            };
            userCache.set(userId, userData);
            setUserData(userData);
          }
        }
      } catch (error) {
        console.error('[MentionTag] Failed to fetch user:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [userId, token]);

  const displayName = loading ? '...' : (userData?.name || 'Tripulante');
  const username = userData?.username || userId.slice(0, 8);

  return (
    <UserProfilePopover userId={userId} username={username}>
      <span className="message-mention">@{displayName}</span>
    </UserProfilePopover>
  );
}

/**
 * Convert @username in input to <@userId>
 * Used when sending messages
 *
 * Example:
 * Input: "Oi @teodoro tudo bem?"
 * Output: "Oi <@uuid-123> tudo bem?"
 */
export function convertMentionsToIds(content: string, userMap: Map<string, string>): string {
  const mentionRegex = /@([a-z0-9_.]+)/g;

  return content.replace(mentionRegex, (match, username) => {
    const userId = userMap.get(username);
    if (userId) {
      return `<@${userId}>`;
    }
    return match; // Keep original if user not found
  });
}
